import React, { useMemo } from 'react';
import MetricsBar from './MetricsBar';
import { formatDisplayDate } from 'utils/dateDisplay';

const METRIC_LABEL = {
  spend: 'Gasto',
  spend_48h: 'Gasto (48h)',
  spend_7d: 'Gasto (7d)',
  spend_14d: 'Gasto (14d)',
  conversions: 'Conv.',
  conversions_48h: 'Conv. (48h)',
  conversions_7d: 'Conv. (7d)',
  conversions_14d: 'Conv. (14d)',
  conversions_30d: 'Conv. (30d)',
  cpa: 'CPA',
  cpa_48h: 'CPA (48 h)',
  cpa_7d: 'CPA (7d)',
  cpa_14d: 'CPA prom. (14 días)',
  cpa_30d: 'CPA (30d)',
  roas: 'ROAS',
  roas_48h: 'ROAS (48h)',
  roas_7d: 'ROAS (7d)',
  roas_14d: 'ROAS (14d)',
  roas_30d: 'ROAS (30d)',
  inline_link_click_ctr: 'CTR',
  inline_link_click_ctr_48h: 'CTR (48h)',
  inline_link_click_ctr_7d: 'CTR (7d)',
  inline_link_click_ctr_14d: 'CTR (14d)',
  inline_link_click_ctr_prev_7d: 'CTR (7d prev.)',
  inline_link_click_ctr_ratio_7d_prev_7d: 'CTR 7d / prev.',
  ctr: 'CTR',
  frequency: 'Freq.',
  frequency_48h: 'Freq. (48h)',
  frequency_7d: 'Freq. (7d)',
  frequency_14d: 'Freq. (14d)',
  impressions: 'Impr.',
  impressions_3d: 'Impr. (3d)',
  budget_utilization: 'Uso',
  daily_budget: 'Budget',
};

const formatPeriodLabel = (analysisWindow) => {
  if (!analysisWindow || typeof analysisWindow !== 'object') return null;
  // Treat start/end as calendar dates (date-only ISO) — formatted in UTC so they
  // don't shift a day in negative-offset timezones. See utils/dateDisplay.
  const start = formatDisplayDate(analysisWindow.start, { day: 'numeric', month: 'short' });
  const end = formatDisplayDate(analysisWindow.end, { day: 'numeric', month: 'short' });
  if (start && end) return `Del ${start} al ${end}`;
  if (analysisWindow.label) return String(analysisWindow.label);
  return null;
};

const WINDOW_SUFFIX_RE = /_(?:48h|7d|14d|30d|3d)$/;
function stripWindow(key) {
  return String(key || '').replace(WINDOW_SUFFIX_RE, '');
}

function inferHint(key) {
  if (key === 'inline_link_click_ctr_ratio_7d_prev_7d') return 'ratio_pct';
  if (key === 'inline_link_click_ctr_prev_7d') return 'percentage_2d';
  const base = stripWindow(key);
  if (base === 'spend' || base === 'daily_budget') return 'currency';
  if (base === 'cpa') return 'currency_decimal';
  if (base === 'conversions' || base === 'impressions') return 'integer';
  if (base === 'roas') return 'ratio';
  if (base === 'frequency') return 'decimal_1';
  if (base === 'ctr' || base === 'inline_link_click_ctr') return 'percentage_2d';
  return 'decimal_2';
}

export default function ContextMetricsBar({ analysisWindow, metrics, currency, metricKeysToShow, className = '' }) {
  const period = useMemo(() => formatPeriodLabel(analysisWindow), [analysisWindow]);
  const m = metrics && typeof metrics === 'object' ? metrics : {};
  const keys = Array.isArray(metricKeysToShow) ? metricKeysToShow : [];

  const items = keys
    .map((k) => {
      const v = m?.[k];
      if (v == null) return null;
      return {
        key: k,
        label: METRIC_LABEL[k] || k,
        value: v,
        hint: inferHint(k),
        currency,
      };
    })
    .filter(Boolean);

  const prefix = period ? (
    <>
      Período:{' '}
      <span className="text-white font-medium tabular-nums">{period}</span>
    </>
  ) : null;

  return <MetricsBar prefix={prefix} items={items} className={`mb-4 ${className}`} />;
}
